// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — array category descriptors
// ═══════════════════════════════════════════════════════════════════════════
// ARRAYRECT  — `geometry/create-arrayrect`
// ARRAYPOLAR — `geometry/create-arraypolar`
// ARRAYPATH  — `geometry/create-arraypath`
// ARRAYEDIT  — `geometry/arrayedit`
// Reducer locations were located via
// `grep -n 'case "geometry/create-array*"' apps/web/src/app/state/geometryReducer.ts`;
// underlying handlers live in `arrayReducers.ts`.
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const GEOMETRY_REDUCER = "apps/web/src/app/state/geometryReducer.ts";

export const ARRAY_DESCRIPTORS: readonly CommandDescriptor[] = [
  // ARRAYRECT — `normalizeDraftCommand` maps both ARRAY and ARRAYRECT
  // to the rectangular flavor (legacy AutoCAD ARRAY default).
  {
    id: "ARRAYRECT",
    category: "array",
    status: "implemented",
    aiCallable: true,
    // D5 — "AR" is App.tsx's shell-keyboard shorthand (shorter than the
    // documented "AR-R" AutoCAD dash-form), present in
    // shellKeyboardCommandAliases but missing here.
    aliases: { en: ["ARRAYRECT", "ARRAY", "AR-R", "AR"], ru: ["МАССИВПР"] },
    commandTypeLiterals: ["geometry/create-arrayrect"],
    draftCommandId: "ARRAYRECT",
    draftSteps: [
      {
        stepId: "arrayrect-select",
        promptKeyRu: "prompt.arrayrect.select",
        promptKeyEn: "prompt.arrayrect.select",
        expects: "selection",
      },
      {
        stepId: "arrayrect-base-point",
        promptKeyRu: "prompt.arrayrect.base",
        promptKeyEn: "prompt.arrayrect.base",
        expects: "point",
      },
      {
        stepId: "arrayrect-count",
        promptKeyRu: "prompt.arrayrect.count",
        promptKeyEn: "prompt.arrayrect.count",
        expects: "text",
      },
      {
        stepId: "arrayrect-spacing",
        promptKeyRu: "prompt.arrayrect.spacing",
        promptKeyEn: "prompt.arrayrect.spacing",
        expects: "text",
      },
    ],
    parameters: [
      {
        name: "entityIds",
        type: "entityIdList",
        required: true,
        description: {
          ru: "Сущности-источники, размножаемые в массив (минимум одна).",
          en: "Source entities to be replicated into the array (at least one).",
        },
      },
      {
        name: "basePoint",
        type: "point2d",
        required: true,
        description: {
          ru: "Базовая точка массива (привязка для строк/столбцов).",
          en: "Base point of the array (anchor for rows/columns).",
        },
      },
      {
        name: "rowCount",
        type: "number",
        required: true,
        description: {
          ru: "Количество строк (положительное целое).",
          en: "Number of rows (positive integer).",
        },
      },
      {
        name: "columnCount",
        type: "number",
        required: true,
        description: {
          ru: "Количество столбцов (положительное целое).",
          en: "Number of columns (positive integer).",
        },
      },
      {
        name: "rowSpacing",
        type: "number",
        required: true,
        description: {
          ru: "Шаг между строками (отрицательное значение направит вниз).",
          en: "Row-to-row spacing (negative value directs downward).",
        },
      },
      {
        name: "columnSpacing",
        type: "number",
        required: true,
        description: {
          ru: "Шаг между столбцами (отрицательное — влево).",
          en: "Column-to-column spacing (negative — leftward).",
        },
      },
      {
        name: "axisAngle",
        type: "number",
        required: false,
        defaultValue: "0",
        description: {
          ru: "Поворот сетки массива в радианах.",
          en: "Rotation of the array grid, in radians.",
        },
      },
    ],
    help: {
      ru: {
        short: "Прямоугольный (ассоциативный) массив выбранных сущностей.",
        long: "Команда ARRAYRECT (МАССИВПР) создаёт прямоугольный ассоциативный массив выбранных сущностей: задаются базовая точка, количество строк и столбцов, шаги по строкам и столбцам, и опциональный угол поворота всей сетки. Массив остаётся ассоциативным — параметры можно изменять через ARRAYEDIT, копии связаны с исходными сущностями.",
        examples: ["ARRAYRECT", "МАССИВПР"],
        seeAlso: ["ARRAYPOLAR", "ARRAYPATH", "ARRAYEDIT"],
      },
      en: {
        short: "Rectangular (associative) array of the selected entities.",
        long: "The ARRAYRECT command builds a rectangular associative array of the selected entities: the user specifies a base point, the row and column counts, the row/column spacings, and an optional rotation angle for the whole grid. The array stays associative — parameters can be edited later via ARRAYEDIT, and the copies remain linked to the source entities.",
        examples: ["ARRAYRECT"],
        seeAlso: ["ARRAYPOLAR", "ARRAYPATH", "ARRAYEDIT"],
      },
    },
    // No dedicated arrayrect label key in i18n yet — reuse the arrayedit
    // label so the UI surface stays bilingual until a key is created.
    uiMetadata: {
      labelKeyRu: "command.arrayedit.label",
      labelKeyEn: "command.arrayedit.label",
      ribbonTab: "modify",
      ribbonSection: "modify",
      glyph: null,
    },
    reducerLocations: [
      {
        commandTypeLiteral: "geometry/create-arrayrect",
        file: GEOMETRY_REDUCER,
      },
    ],
  },

  {
    id: "ARRAYPOLAR",
    category: "array",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
    // (ТЗ §10.2 actively defers polar/path arrays to future scope).
    aiCallable: false,
    aliases: { en: ["ARRAYPOLAR", "ARRAYP", "AP", "AR-P"], ru: ["МАССИВКР"] },
    commandTypeLiterals: ["geometry/create-arraypolar"],
    draftCommandId: "ARRAYPOLAR",
    draftSteps: [
      {
        stepId: "arraypolar-select",
        promptKeyRu: "prompt.arraypolar.select",
        promptKeyEn: "prompt.arraypolar.select",
        expects: "selection",
      },
      {
        stepId: "arraypolar-center",
        promptKeyRu: "prompt.arraypolar.center",
        promptKeyEn: "prompt.arraypolar.center",
        expects: "point",
      },
      {
        stepId: "arraypolar-count",
        promptKeyRu: "prompt.arraypolar.count",
        promptKeyEn: "prompt.arraypolar.count",
        expects: "text",
      },
      {
        stepId: "arraypolar-fill",
        promptKeyRu: "prompt.arraypolar.fill",
        promptKeyEn: "prompt.arraypolar.fill",
        expects: "text",
      },
    ],
    parameters: [
      {
        name: "entityIds",
        type: "entityIdList",
        required: true,
        description: {
          ru: "Сущности-источники массива (минимум одна).",
          en: "Source entities of the array (at least one).",
        },
      },
      {
        name: "centerPoint",
        type: "point2d",
        required: true,
        description: {
          ru: "Центр полярного (кругового) массива.",
          en: "Center point of the polar (angular) array.",
        },
      },
      {
        name: "itemCount",
        type: "number",
        required: true,
        description: {
          ru: "Количество элементов массива (минимум 2).",
          en: "Number of items in the array (minimum 2).",
        },
      },
      {
        name: "fillAngle",
        type: "number",
        required: false,
        defaultValue: "360",
        description: {
          ru: "Угол заполнения в градусах (360 — полный круг, отрицательное — по часовой стрелке).",
          en: "Fill angle in degrees (360 — full circle, negative — clockwise).",
        },
      },
      {
        name: "rotateItems",
        type: "boolean",
        required: false,
        defaultValue: "true",
        description: {
          ru: "Поворачивать ли каждый элемент по касательной к окружности.",
          en: "Whether to rotate each item to be tangent to the circle.",
        },
      },
      {
        name: "rowCount",
        type: "number",
        required: false,
        defaultValue: "1",
        description: {
          ru: "Количество концентрических колец (для многорядного полярного массива).",
          en: "Number of concentric rings (for multi-row polar arrays).",
        },
      },
      {
        name: "rowSpacing",
        type: "number",
        required: false,
        defaultValue: "0",
        description: {
          ru: "Радиальный шаг между кольцами.",
          en: "Radial spacing between rings.",
        },
      },
    ],
    help: {
      ru: {
        short: "Полярный (круговой) ассоциативный массив выбранных сущностей.",
        long: "Команда ARRAYPOLAR (МАССИВКР) создаёт круговой ассоциативный массив выбранных сущностей вокруг центра: задаются количество элементов, угол заполнения (360° — полный круг), флаг поворота каждого элемента по касательной, опциональные кольца (rowCount > 1) и радиальный шаг между ними. Массив ассоциативный — редактируется через ARRAYEDIT.",
        examples: ["ARRAYPOLAR", "МАССИВКР", "AP"],
        seeAlso: ["ARRAYRECT", "ARRAYPATH", "ARRAYEDIT"],
      },
      en: {
        short: "Polar (angular) associative array of the selected entities.",
        long: "The ARRAYPOLAR command creates a polar associative array of the selected entities around a center point: the user specifies the item count, the fill angle (360° = full circle), whether to rotate each item to follow the tangent, optional concentric rings (rowCount > 1) and a radial spacing between them. The array stays associative — edit later through ARRAYEDIT.",
        examples: ["ARRAYPOLAR", "AP"],
        seeAlso: ["ARRAYRECT", "ARRAYPATH", "ARRAYEDIT"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.arraypolar.label",
      labelKeyEn: "command.arraypolar.label",
      ribbonTab: "modify",
      ribbonSection: "modify",
      glyph: null,
    },
    reducerLocations: [
      {
        commandTypeLiteral: "geometry/create-arraypolar",
        file: GEOMETRY_REDUCER,
      },
    ],
  },

  {
    id: "ARRAYPATH",
    category: "array",
    status: "implemented",
    // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
    // (ТЗ §10.2 actively defers polar/path arrays to future scope).
    aiCallable: false,
    aliases: { en: ["ARRAYPATH", "AR-PA"], ru: ["МАССИВПУ"] },
    commandTypeLiterals: ["geometry/create-arraypath"],
    draftCommandId: "ARRAYPATH",
    draftSteps: [
      {
        stepId: "arraypath-select",
        promptKeyRu: "prompt.arraypath.select",
        promptKeyEn: "prompt.arraypath.select",
        expects: "selection",
      },
      {
        stepId: "arraypath-path",
        promptKeyRu: "prompt.arraypath.path",
        promptKeyEn: "prompt.arraypath.path",
        expects: "selection",
      },
      {
        stepId: "arraypath-method",
        promptKeyRu: "prompt.arraypath.method",
        promptKeyEn: "prompt.arraypath.method",
        expects: "keyword",
        keywords: ["D", "M"],
      },
      {
        stepId: "arraypath-params",
        promptKeyRu: "prompt.arraypath.params",
        promptKeyEn: "prompt.arraypath.params",
        expects: "text",
      },
    ],
    parameters: [
      {
        name: "entityIds",
        type: "entityIdList",
        required: true,
        description: {
          ru: "Сущности-источники массива.",
          en: "Source entities of the array.",
        },
      },
      {
        name: "pathEntityId",
        type: "entityId",
        required: true,
        description: {
          ru: "Идентификатор сущности-пути (LINE / ARC / CIRCLE / LWPOLYLINE / SPLINE / ELLIPSE).",
          en: "Identifier of the path entity (LINE / ARC / CIRCLE / LWPOLYLINE / SPLINE / ELLIPSE).",
        },
      },
      {
        name: "method",
        type: "enum",
        enumValues: ["divide", "measure"],
        required: true,
        description: {
          ru: "divide — разделить путь на itemCount равных интервалов; measure — расставить элементы с шагом itemSpacing.",
          en: "divide — split the path into itemCount equal intervals; measure — place items at itemSpacing intervals.",
        },
      },
      {
        name: "itemCount",
        type: "number",
        required: false,
        description: {
          ru: "Количество элементов (для method='divide'). Обязательно ровно одно из itemCount/itemSpacing.",
          en: "Number of items (for method='divide'). Exactly one of itemCount/itemSpacing is required.",
        },
      },
      {
        name: "itemSpacing",
        type: "number",
        required: false,
        description: {
          ru: "Шаг между элементами вдоль пути (для method='measure'). Положительное число.",
          en: "Spacing between items along the path (for method='measure'). Positive number.",
        },
      },
      {
        name: "alignItems",
        type: "boolean",
        required: false,
        defaultValue: "true",
        description: {
          ru: "Ориентировать ли каждый элемент по касательной к пути.",
          en: "Whether to orient each item along the path tangent.",
        },
      },
    ],
    help: {
      ru: {
        short: "Массив сущностей вдоль траектории-пути.",
        long: "Команда ARRAYPATH (МАССИВПУ) распределяет копии выбранных сущностей вдоль выбранной траектории (LINE, ARC, CIRCLE, LWPOLYLINE, SPLINE, ELLIPSE). Метод divide делит путь на itemCount равных частей, метод measure расставляет элементы с шагом itemSpacing. Флаг alignItems позволяет поворачивать каждый элемент по касательной к пути. Массив ассоциативный.",
        examples: ["ARRAYPATH", "МАССИВПУ"],
        seeAlso: ["ARRAYRECT", "ARRAYPOLAR", "ARRAYEDIT"],
      },
      en: {
        short: "Array of entities distributed along a path.",
        long: "The ARRAYPATH command distributes copies of the selected entities along the chosen path entity (LINE, ARC, CIRCLE, LWPOLYLINE, SPLINE, ELLIPSE). Method divide splits the path into itemCount equal segments; method measure places items at itemSpacing intervals. The alignItems flag controls whether each item is rotated to the path tangent. The array stays associative.",
        examples: ["ARRAYPATH"],
        seeAlso: ["ARRAYRECT", "ARRAYPOLAR", "ARRAYEDIT"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.arraypath.label",
      labelKeyEn: "command.arraypath.label",
      ribbonTab: "modify",
      ribbonSection: "modify",
      glyph: null,
    },
    reducerLocations: [
      {
        commandTypeLiteral: "geometry/create-arraypath",
        file: GEOMETRY_REDUCER,
      },
    ],
  },

  {
    id: "ARRAYEDIT",
    category: "array",
    status: "implemented",
    aiCallable: true,
    aliases: { en: ["ARRAYEDIT", "AE"], ru: ["МАССИВРЕД"] },
    commandTypeLiterals: ["geometry/arrayedit"],
    draftCommandId: "ARRAYEDIT",
    draftSteps: [
      {
        stepId: "arrayedit-select",
        promptKeyRu: "prompt.arrayedit.select",
        promptKeyEn: "prompt.arrayedit.select",
        expects: "selection",
      },
    ],
    parameters: [
      {
        name: "arrayGroupId",
        type: "string",
        required: true,
        description: {
          ru: "Идентификатор ассоциативной группы массива (ставится при создании ARRAY*).",
          en: "Associative array group identifier (assigned when ARRAY* is created).",
        },
      },
      {
        name: "mode",
        type: "enum",
        enumValues: ["rect", "polar", "path"],
        required: true,
        description: {
          ru: "Тип редактируемого массива — соответствует команде, его создавшей.",
          en: "Kind of array being edited — matches the command that created it.",
        },
      },
      {
        name: "rect",
        type: "object",
        required: false,
        description: {
          ru: "Параметры прямоугольного массива {rowCount, columnCount, rowSpacing, columnSpacing, axisAngle}.",
          en: "Rectangular array parameters {rowCount, columnCount, rowSpacing, columnSpacing, axisAngle}.",
        },
      },
      {
        name: "polar",
        type: "object",
        required: false,
        description: {
          ru: "Параметры полярного массива {centerPoint, itemCount, fillAngle, rotateItems, rowCount, rowSpacing}.",
          en: "Polar array parameters {centerPoint, itemCount, fillAngle, rotateItems, rowCount, rowSpacing}.",
        },
      },
      {
        name: "path",
        type: "object",
        required: false,
        description: {
          ru: "Параметры массива по пути {method, itemCount?, itemSpacing?, alignItems, pathEntityId}.",
          en: "Path array parameters {method, itemCount?, itemSpacing?, alignItems, pathEntityId}.",
        },
      },
    ],
    help: {
      ru: {
        short:
          "Редактирование параметров существующего ассоциативного массива.",
        long: "Команда ARRAYEDIT (МАССИВРЕД) выбирает любую копию ассоциативного массива и открывает редактирование параметров той группы, к которой она принадлежит. Передаются те же поля, что и в командах создания (ARRAYRECT/ARRAYPOLAR/ARRAYPATH), но в одной структуре; ровно одно из rect/polar/path должно быть заполнено в соответствии с mode. Массив пересчитывается, копии остаются ассоциированы с исходными сущностями.",
        examples: ["ARRAYEDIT", "МАССИВРЕД", "AE"],
        seeAlso: ["ARRAYRECT", "ARRAYPOLAR", "ARRAYPATH"],
      },
      en: {
        short: "Edit parameters of an existing associative array.",
        long: "The ARRAYEDIT command selects any copy in an associative array and opens parameter editing for the group that copy belongs to. The payload mirrors the create-commands (ARRAYRECT/ARRAYPOLAR/ARRAYPATH) but bundled into one structure; exactly one of rect/polar/path must be supplied, matching mode. The array is recomputed and copies stay associated with the source entities.",
        examples: ["ARRAYEDIT", "AE"],
        seeAlso: ["ARRAYRECT", "ARRAYPOLAR", "ARRAYPATH"],
      },
    },
    uiMetadata: {
      labelKeyRu: "command.arrayedit.label",
      labelKeyEn: "command.arrayedit.label",
      ribbonTab: "modify",
      ribbonSection: "modify",
      glyph: null,
    },
    reducerLocations: [
      { commandTypeLiteral: "geometry/arrayedit", file: GEOMETRY_REDUCER },
    ],
  },
];
